import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

// ---------------------------------------------------------------------------
// CVA variants for TooltipContent
// ---------------------------------------------------------------------------

const tooltipContentVariants = cva(
  // Base styles shared by all variants
  "z-50 overflow-hidden rounded-md text-sm shadow-md",
  {
    variants: {
      variant: {
        // Default: stock shadcn behavior — fully backward compatible
        default: [
          "border bg-popover px-3 py-1.5 text-popover-foreground",
          "animate-in fade-in-0 zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
          "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        ].join(" "),

        // Stats: rich hover card for token badges, LQ scores, price data
        stats: [
          "border border-[#1A1A1A] bg-[#111111] px-4 py-3 text-white",
          "shadow-[0_4px_24px_rgba(0,0,0,0.5)]",
          "animate-in fade-in-0 zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
          "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        ].join(" "),

        // Animated: spring entrance inspired by Aceternity UI (tooltip-16)
        animated: [
          "border border-[#1A1A1A] bg-[#111111] px-3 py-1.5 text-white",
          "shadow-[0_4px_24px_rgba(0,0,0,0.5)]",
          "data-[state=open]:animate-tooltip-spring-in",
          "data-[state=closed]:animate-tooltip-spring-out",
        ].join(" "),
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

// ---------------------------------------------------------------------------
// TooltipContent
// ---------------------------------------------------------------------------

export interface TooltipContentProps
  extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>,
    VariantProps<typeof tooltipContentVariants> {}

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, variant, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(tooltipContentVariants({ variant }), className)}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// ---------------------------------------------------------------------------
// TooltipStatsContent — structured stat grid for hover cards (tooltip-12)
// ---------------------------------------------------------------------------

interface TooltipStatItem {
  label: string;
  value: string | number;
  /** Optional change indicator, e.g. "+12.5%" */
  change?: string;
  /** Positive = green, negative = red, undefined = neutral */
  changeDirection?: "positive" | "negative";
}

interface TooltipStatsContentProps
  extends Omit<TooltipContentProps, "variant" | "children"> {
  /** Title displayed at the top of the stats card */
  title?: string;
  /** Array of stat items to display in a grid */
  stats: TooltipStatItem[];
  /** Footer text below the stats grid */
  footer?: React.ReactNode;
}

const TooltipStatsContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipStatsContentProps
>(({ className, title, stats, footer, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        tooltipContentVariants({ variant: "stats" }),
        "min-w-[200px] max-w-[280px]",
        className
      )}
      {...props}
    >
    {title && (
      <div className="mb-2 text-xs font-medium text-[#CCFF00]">{title}</div>
    )}
    <div
      className={cn(
        "grid gap-x-4 gap-y-1.5",
        stats.length <= 2 ? "grid-cols-2" : "grid-cols-2"
      )}
    >
      {stats.map((stat) => (
        <div key={stat.label} className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-white/50">
            {stat.label}
          </span>
          <div className="flex items-baseline gap-1.5">
            <span className="text-sm font-semibold text-white">
              {stat.value}
            </span>
            {stat.change && (
              <span
                className={cn(
                  "text-[10px] font-medium",
                  stat.changeDirection === "positive" && "text-green-400",
                  stat.changeDirection === "negative" && "text-red-400",
                  !stat.changeDirection && "text-white/50"
                )}
              >
                {stat.change}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
    {footer && (
      <div className="mt-2 border-t border-white/10 pt-2 text-[10px] text-white/40">
        {footer}
      </div>
    )}
    </TooltipPrimitive.Content>
  </TooltipPrimitive.Portal>
));
TooltipStatsContent.displayName = "TooltipStatsContent";

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipStatsContent,
  tooltipContentVariants,
};
